import {flow, getEnv, getType, resolveIdentifier, types} from "mobx-state-tree";
import { AuditMetaData } from "../AuditMetaData";
import {Utm} from "../utms/UtmModel";
import {ParameterSet} from "../parameterSets/ParameterSetModel";
import QrConfiguration from "../QrConfiguration";
import Notification from "../../utils/Notification";
import {ExtendedDataSetModel} from "../extendedData/ExtendedDataSet";

export function createDefaultSettingModel(item, target) {
  const utm = item?.utm?.map(obj => {return resolveIdentifier(Utm, target, obj?.uuid || obj)}).shift();
  const parameterset = item?.parametersets?.map(parameterset => {return resolveIdentifier(ParameterSet, target, parameterset?.uuid || parameterset)}).shift();
  const designerQR = item?.designerConfig?.map(obj => {return resolveIdentifier(QrConfiguration,target,obj?.uuid || obj)}).shift();
  const extendedDataSet = item?.extendedDataSet?.map(obj => {return resolveIdentifier(ExtendedDataSetModel,target,obj?.uuid || obj)}).shift();
  return DefaultSettingModel.create({
    uuid: item?.uuid,
    name: item?.name,
    groups: item?.groups,
    dimensions: item?.dimensions,
    utm: utm,
    parametersets: parameterset,
    designerConfig: designerQR ,
    extendedDataSet: extendedDataSet,
    hashValue:item?.hashValue,
    status: item?.status,
    _audit: item?._audit,
    updatedAt: item?.updatedAt,
    createdAt: item?.createdAt,
    types: item?.types,
  });
}

export const DefaultSettingModel = types
  .model({
    uuid: types.identifier,
    name: types.optional(types.string, ""),
    groups: types.maybe(types.array(types.string)),
    dimensions: types.maybe(types.frozen()),
    types: types.maybe(types.array(types.string)),
    utm: types.maybeNull(types.safeReference(types.late(() =>Utm))),
    parametersets: types.maybeNull(types.safeReference(types.late(() =>ParameterSet))),
    designerConfig: types.maybeNull(types.safeReference(types.late(() =>QrConfiguration))),
    extendedDataSet: types.maybeNull(types.safeReference(types.late(() =>ExtendedDataSetModel))),
    hashValue: types.maybe(types.string),
    status: types.maybe(types.string),
    _audit: types.maybeNull(AuditMetaData),
    createdAt: types.maybeNull(types.string),
    updatedAt: types.maybeNull(types.string),
  })
  .actions((self) => ({
    updateName(newName) {
      self.name = newName;
    },
    updateGroups(newGroups) {
      self.groups = newGroups;
    },
    updateTypes(types){
      self.types = types
    },
    updateUTM(newUTM) {
      self.utm = newUTM
    },
    updateParameterSet(newParameterSet){
      self.parametersets=newParameterSet
    },
    updateDesignerConfig(newDesignerConfig){
      self.designerConfig=newDesignerConfig
    },
    updateDimensions(newDimensions) {
      self.dimensions = newDimensions;
    },
    updateExtendedDataSet(newExtendedDataSet){
      self.extendedDataSet= newExtendedDataSet
    },
    hydrateDefaultSetting(setting, target) {
        const utm = setting?.utm ? resolveIdentifier(Utm, target, setting?.utm?.uuid || setting?.utm) : setting?.utm;
        const parameterset = setting?.parametersets ? resolveIdentifier(ParameterSet, target, setting?.parametersets?.uuid || setting?.parametersets) : setting?.parametersets;
        const designerQR = setting?.designerConfig ? resolveIdentifier(QrConfiguration, target, setting?.designerConfig?.uuid || setting?.designerConfig) : setting?.designerConfig;
        designerQR?.setQrConfig(setting?.designerConfig?.data)
        const extendedDataSet = setting?.extendedDataSet ? resolveIdentifier(ExtendedDataSetModel,target,setting?.extendedDataSet?.uuid || setting?.extendedDataSet) : setting?.extendedDataSet;
        self.name = setting?.name;
        self.groups = setting?.groups;
        self.dimensions = setting?.dimensions;
        self.utm = utm;
        self.parametersets = parameterset;
        self.designerConfig = designerQR;
        self.extendedDataSet = extendedDataSet;
        self.hashValue = setting?.hashValue;
        self.status = setting?.status;
        self._tenantUuid = setting?._tenantUuid;
        self._audit = setting?._audit;
        self.types = setting?.types;
    },
    updateInput(inputType, e) {
      self[inputType] = e.target.value
      if (inputType === 'name') {
        self[`${inputType}IsInvalid`] = !self.doesInputHaveLength(inputType)
      }
    },
    setNameIsInvalid(bool) {
      self.nameIsInvalid = bool
    },
    validateDefaultSettingModel(){
    let isValid = false;
      if (self.utm || self.designerConfig || self.parametersets || self.extendedDataSet) {
        isValid=true
      }
      if(self.groups && self.groups.length>0){
        isValid = isValid && true
      }
      if (!isValid) {
        console.warn("Validation failed: At least one of 'utm', 'designerConfig', or 'parametersets' must be present.");
      }
      return isValid;
    },
    getDefaultSetting: flow(function* f(uuid) {
        const manager = getEnv(uuid).defaultSettingManager
        const resp = yield manager.getDefaultSetting(uuid);
        if (resp.status === 200) {
          return resp
        } else {
          new Notification()
            .setType("error")
            .setMessage(`Unable to retrieve Default Setting`)
            .send();
        }
    }),
    fetchModelAndHydrate: flow(function* f(query={}){
      if (!query) {
        console.warn(`${getType(self).name} Warn: Query is 'undefined' or 'null'`);
      }
      let response;
      try {
        response = yield getEnv(self).defaultSettingManager.fetchDefaultSetting(query);
        self?.hydrateDefaultSetting(response,self)
      } catch (e) {
        console.error(`Error: Failed to Fetch and Hydrate`, e);
      }
      return response;
    }),
    clearAllSelections() {
      self.name = "";
      self.groups = undefined;
      self.dimensions = undefined;
      self.types = undefined;
      self.utm = null;
      self.parametersets = null;
      self.designerConfig = null;
      self.extendedDataSet = null;
      self.hashValue = undefined;
      self.status = undefined;
      self._audit = null;
      console.log('DefaultSettingModel: All selections cleared');
    },


  }))
  .views((self) => ({
    isActive() {
      return self.status === "ACTIVE";
    },
    belongsToGroup(groupId) {
      return self.groups.includes(groupId);
    },
    isInputInvalid(inputType) {
      return self[`${inputType}IsInvalid`]
    },
    doesInputHaveLength(inputType) {
      return self[inputType]?.length > 0;
    },

  }));
